import Image from 'next/image';
import { useRouter } from 'next/router';
import { type ComponentPropsWithoutRef, forwardRef, memo, useCallback, useMemo } from 'react';

import { Copy, GoTo } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { useToasts } from '@air/provider-toast';
import { type FullWorkspace } from '@air/types';

import { useWorkspacesSwitchWorkspacseMutation } from '~/components/v2/hooks/queries/mutations/useWorkspacesSwitchWorkspacseMutation';
import { getVercelEnv } from '~/components/v2/utils/env/getVercelEnv';
import { Pathname } from '~/constants/pathname';
import { Routes } from '~/constants/routes';
import { useCopyTextToClipboard } from '~/hooks/useCopyTextToClipboard';

export type InternalBrowseWorkspaceModalListItemProps = ComponentPropsWithoutRef<'div'> & {
  onSwitchWorkspace?: ({ workspace }: { workspace: FullWorkspace }) => void;
  workspace: FullWorkspace;
};

export const InternalBrowseWorkspaceModalListItem = memo(
  forwardRef<HTMLDivElement, InternalBrowseWorkspaceModalListItemProps>(
    ({ onSwitchWorkspace: onSwitchWorkspaceProp, workspace, ...restOfProps }, ref) => {
      const router = useRouter();
      const { showToast } = useToasts();
      const { copyTextToClipboard } = useCopyTextToClipboard();
      const { mutate: switchWorkspace } = useWorkspacesSwitchWorkspacseMutation();

      const copyLabel = useMemo(() => `Copy workspace ID for ${workspace.name} to clipboard`, [workspace.name]);

      const onViewInPortal = useCallback(() => {
        const url = `${Routes.portal[getVercelEnv(process.env.NEXT_PUBLIC_VERCEL_ENV)]}/workspace/${workspace.id}`;

        window.open(url, '_blank');
      }, [workspace.id]);

      const onCopyWorkspaceId = useCallback(() => {
        copyTextToClipboard({
          text: workspace.id,
          successMessage: `Workspace ID for ${workspace.name} copied to clipboard`,
          errorMessage: 'Failed to copy workspace ID to clipboard',
        });
      }, [copyTextToClipboard, workspace.id, workspace.name]);

      const onSwitchWorkspace = useCallback(() => {
        switchWorkspace(
          { workspaceId: workspace.id },
          {
            onSuccess: () => {
              router.push({
                pathname: Pathname.Search,
              });

              showToast(`Switched to ${workspace.name}`, {
                color: 'black',
              });

              onSwitchWorkspaceProp?.({ workspace });
            },
            onError: () => {
              showToast('Failed to switch workspace', {
                color: 'red',
              });
            },
          },
        );
      }, [onSwitchWorkspaceProp, router, showToast, switchWorkspace, workspace]);

      return (
        <div
          className="flex w-full items-center gap-2 border-b border-b-grey-5 py-3 text-left"
          data-workspace-id={workspace.id}
          ref={ref}
          {...restOfProps}
        >
          <div className="relative size-10 overflow-hidden rounded-8">
            <Image alt={`Logo for ${workspace.name}`} className="object-fill" fill sizes="96px" src={workspace.image} />
          </div>
          <div className="flex grow flex-col gap-0.5">
            <h3 className="truncate font-semibold text-14 text-grey-12">{workspace.name}</h3>
            <p className="flex items-center gap-1 truncate text-12 text-grey-12">
              <span className="select-all">{workspace.id}</span>
              <span>&middot;</span>
              <span className="capitalize">{workspace.plan}</span>
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Tooltip label={copyLabel}>
              <IconButton appearance="filled" color="grey" onClick={onCopyWorkspaceId} label={copyLabel} size="small">
                <Copy className="size-4" />
              </IconButton>
            </Tooltip>
            <Button
              appearance="filled"
              color="grey"
              onClick={onViewInPortal}
              prefix={<GoTo className="size-3" />}
              size="small"
            >
              Portal
            </Button>
            <Button appearance="filled" color="grey" onClick={onSwitchWorkspace} size="small">
              Switch
            </Button>
          </div>
        </div>
      );
    },
  ),
);

InternalBrowseWorkspaceModalListItem.displayName = 'InternalBrowseWorkspaceModalListItem';
